import { type SiteMapItem, providerSiteMap } from "@/config/sitemap";

import { itemModulesEnabled, routeModulesEnabled } from "@/lib/module-gate";
import { hasAnyRole, hasPagePermissions } from "@/lib/permissions";
import { tenantMatchesAllowedSubDomains } from "@/lib/tenant-flags";

import type { ProviderSummary } from "@/types/provider";
import type { Module } from "@/types/tenant";

/**
 * Filter sitemap items by role, permission, enabled-module, and tenant access.
 * Parents without direct access remain as non-navigable groups when they have
 * accessible children.
 */
export function filterItemsByAccess(
  items: SiteMapItem[],
  currentUser: ProviderSummary | null | undefined,
  currentRoute: string | undefined,
  enabledModules?: Module[],
  tenantSubDomain?: string,
): SiteMapItem[] {
  if (!currentUser) return [];

  return items
    .map((item) => {
      if (enabledModules && !itemModulesEnabled(item, enabledModules)) {
        return null;
      }
      if (
        !tenantMatchesAllowedSubDomains({
          tenantSubDomain,
          allowedSubDomains: item.tenantSubDomains,
          includeAllInDevelopment: false,
        })
      ) {
        return null;
      }

      const hasRequiredRoles =
        !item.roles || hasAnyRole(currentUser, item.roles);
      const hasRequiredPermissions = hasPagePermissions(currentUser, item);
      const routeOpen =
        !enabledModules || routeModulesEnabled(item, enabledModules);
      const hasAccess = hasRequiredRoles && hasRequiredPermissions && routeOpen;

      const accessibleChildren = item.children
        ? filterItemsByAccess(
            item.children,
            currentUser,
            currentRoute,
            enabledModules,
            tenantSubDomain,
          )
        : [];

      const isActive = currentRoute === item.route;
      if (item.hidden && !isActive) {
        return null;
      }

      if (item.children !== undefined) {
        if (accessibleChildren.length === 0) {
          if (item.children.length === 0 || !item.route || !hasAccess) {
            return null;
          }
          return { ...item, children: [] };
        }

        if (!hasAccess) {
          return {
            ...item,
            children: accessibleChildren,
            route: undefined,
            _parentRouteRemoved: true,
          };
        }

        return { ...item, children: accessibleChildren };
      }

      return hasAccess ? item : null;
    })
    .filter((item): item is SiteMapItem => item !== null);
}

/**
 * Whether the user can reach any page in the main console.
 *
 * A role scoped entirely to settings (an integrations or developer role, say)
 * clears the sidebar down to the notification bell, which navigates nowhere.
 * Callers use this to send such a user to settings instead of a tenant home
 * page they cannot open.
 */
export function hasConsoleDestination(
  currentUser: ProviderSummary | null | undefined,
  enabledModules?: Module[],
): boolean {
  const reachesARoute = (items: SiteMapItem[]): boolean =>
    items.some(
      (item) => Boolean(item.route) || reachesARoute(item.children ?? []),
    );

  return reachesARoute(
    filterItemsByAccess(
      providerSiteMap,
      currentUser,
      undefined,
      enabledModules,
    ),
  );
}

export interface SectionMenuGroup {
  name: string;
  entries: SiteMapItem[];
}

export interface SectionMenu {
  /** Destinations that sit directly under the section, above its groups. */
  entries: SiteMapItem[];
  groups: SectionMenuGroup[];
}

/**
 * A sub-nav section (Settings, Analytics) flattened for a dropdown: the groups
 * the sidebar shows, holding only destinations this user can open. Derived from
 * the same sitemap config the sidebar reads, so the two cannot drift apart.
 */
export function sectionMenu(
  config: SiteMapItem[],
  currentUser: ProviderSummary | null | undefined,
  options: { enabledModules?: Module[]; tenantSubDomain?: string },
): SectionMenu {
  const [root] = filterItemsByAccess(
    config,
    currentUser,
    undefined,
    options.enabledModules,
    options.tenantSubDomain,
  );

  const visible = root?.children ?? [];

  return {
    entries: visible.filter((item) => !item.children?.length && item.route),
    groups: visible
      .filter((item) => item.children?.length)
      .map((item) => ({
        name: item.name,
        entries: (item.children ?? []).filter((child) => child.route),
      })),
  };
}
